import { DestroyRef, inject, Injectable, InjectionToken, NgZone, signal, DOCUMENT } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ApplicationReloader } from '@/app/application-reloader';
import { ApplicationService } from '@/app/application.service';
import { currentTab } from '@/app/current-tab';
import { extractBuildFingerprint, hasEntryBundle, isNewerBuild, parseBuildFingerprint } from './app-build-fingerprint';
import { AppUpdateDialogComponent, AppUpdateDialogData, AppUpdateReason } from './app-update-dialog.component';

/** Vingerafdruk van de build die deze tab draait, vastgelegd bij het opstarten. */
export const RUNNING_BUILD_FINGERPRINT = new InjectionToken<readonly string[]>('RUNNING_BUILD_FINGERPRINT', {
  providedIn: 'root',
  factory: () => extractBuildFingerprint(inject(DOCUMENT))
});

/**
 * Wat een check aanleidt. Alleen `visible` wordt afgeremd (tabwissels zijn frequent);
 * de andere zijn zeldzaam en betekenisvol.
 */
export type AppUpdateTrigger = 'reconnect' | 'visible' | 'restored' | 'online' | 'chunk';

const MIN_GAP_BETWEEN_VISIBILITY_CHECKS_MS = 60_000;

/**
 * Meldt een tab die een release heeft overleefd dat er een nieuwe versie klaarstaat, en
 * herstart op vraag van de gebruiker (een gewone reload volstaat: index.html wordt nooit
 * bewaard, de gehashte bundles zijn per versie uniek).
 *
 * Bewust GEEN timer: een check volgt enkel op een gebeurtenis die met een release
 * samenvalt of waarna de tab lang stil kan hebben gelegen:
 * - `reconnect`: elke release herstart caddy en/of de api, waardoor de SignalR-verbinding
 *   wegvalt; de herverbinding is in de praktijk het releasesignaal (zie
 *   PlatformInstanceListener). De check zelf loopt over HTTP, niet over SignalR.
 * - `visible` / `restored` / `online`: terug naar de tab, terug uit de back/forward-cache,
 *   weer online (laptopdeksel, gsm-resume: daar lag de verbinding stil).
 * - `chunk`: een lazy chunk van de oude build bestaat niet meer (AppUpdateAwareErrorHandler);
 *   dat onderdeel blijft stuk tot een herstart, dus dan altijd melden (ook na "later").
 *
 * "Later" onthoudt de afgewezen build: de dialoog komt pas terug bij een nóg nieuwere
 * build of een chunk-fout; tot dan blijft de navbar-chip zichtbaar.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly matDialog = inject(MatDialog);
  private readonly reloader = inject(ApplicationReloader);
  private readonly appService = inject(ApplicationService);
  private readonly runningBuild = inject(RUNNING_BUILD_FINGERPRINT);

  private readonly _available = signal(false);
  private readonly _dialogOpen = signal(false);

  /** Er staat een nieuwere versie klaar dan deze tab draait. */
  readonly available = this._available.asReadonly();
  readonly dialogOpen = this._dialogOpen.asReadonly();

  private started = false;
  private readonly cleanups: (() => void)[] = [];
  private inFlight: Promise<void> | undefined = undefined;
  private lastCheckAt = 0;
  private latestBuildKey: string | undefined = undefined;
  /** Versienummer van de klaarstaande build (`app-version.json`); `undefined` = onbekend. */
  private latestVersion: string | undefined = undefined;
  private dismissedBuildKey: string | undefined = undefined;
  /** Een chunk faalde sinds de laatste beslissing: de gebruiker zit vast, dus altijd melden. */
  private chunkFailed = false;
  private dialogRef: MatDialogRef<AppUpdateDialogComponent, boolean> | undefined = undefined;
  private closingOnBehalfOfOtherTab = false;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  /** Lokaal (ng serve) zijn de bundles ongehasht: dan is er niets om te vergelijken. */
  private get detectionEnabled(): boolean {
    return hasEntryBundle(this.runningBuild);
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.listenForDecisionsInOtherTabs();

    if (!this.detectionEnabled) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.listen(this.document, 'visibilitychange', () => {
        if (this.document.visibilityState === 'visible') {
          void this.checkNow('visible');
        }
      });
      this.listen(window, 'pageshow', event => {
        if ((event as PageTransitionEvent).persisted) {
          void this.checkNow('restored');
        }
      });
      this.listen(window, 'online', () => void this.checkNow('online'));
    });
  }

  /** Haalt index.html op en meldt een nieuwere build; gelijktijdige aanvragen delen één fetch. */
  checkNow(trigger: AppUpdateTrigger): Promise<void> {
    if (!this.detectionEnabled) {
      return Promise.resolve();
    }

    if (trigger === 'chunk') {
      this.chunkFailed = true;
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    const now = Date.now();
    if (trigger === 'visible' && now - this.lastCheckAt < MIN_GAP_BETWEEN_VISIBILITY_CHECKS_MS) {
      return Promise.resolve();
    }
    this.lastCheckAt = now;

    this.inFlight = this.fetchLatestBuild()
      .then(latest => this.handleLatestBuild(latest))
      .finally(() => (this.inFlight = undefined));
    return this.inFlight;
  }

  /** Expliciete melding vanuit de backend (PlatformController update-clients). */
  notifyPushedUpdate(): Promise<void> {
    return this.fetchLatestVersion().then(version => {
      this.latestVersion = version ?? this.latestVersion;
      this.zone.run(() => {
        this._available.set(true);
        this.openDialog('update');
      });
    });
  }

  /** Navbar-chip: de eerder uitgestelde melding opnieuw tonen. */
  reopenDialog(): void {
    this.openDialog('update');
  }

  private async handleLatestBuild(latest: string[] | undefined): Promise<void> {
    if (!latest || !isNewerBuild(this.runningBuild, latest)) {
      // Geen nieuwe build: een mislukte chunk had dan een andere oorzaak (netwerk).
      this.chunkFailed = false;
      return;
    }

    const key = latest.join('|');
    // Versienummer enkel opvragen bij een (nieuwe) klaarstaande build: zo kan de dialoog tonen
    // naar welke versie je gaat. Lukt dat niet, dan toont hij gewoon enkel de huidige versie.
    if (key !== this.latestBuildKey || this.latestVersion === undefined) {
      this.latestVersion = await this.fetchLatestVersion();
    }
    this.latestBuildKey = key;

    this.zone.run(() => {
      this._available.set(true);

      if (this.chunkFailed || key !== this.dismissedBuildKey) {
        this.openDialog(this.chunkFailed ? 'chunk' : 'update');
      }
    });
  }

  private async fetchLatestBuild(): Promise<string[] | undefined> {
    try {
      const url = new URL('index.html', this.document.baseURI);
      // Extra cache-buster voor eventuele tussenliggende caches; de server negeert hem.
      url.searchParams.set('update-check', Date.now().toString());
      const response = await fetch(url.toString(), { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) {
        return undefined;
      }
      return parseBuildFingerprint(await response.text());
    } catch {
      // Midden in een deploy of offline: geen oordeel, het volgende signaal probeert opnieuw.
      return undefined;
    }
  }

  /**
   * Versienummer van de build die een refresh zou laden, uit `app-version.json` (geschreven door
   * scripts/write-app-version.js bij elke build). Ontbreekt het (oudere build, midden in een
   * deploy, SPA-fallback naar index.html), dan `undefined`: geen oordeel, enkel geen versie.
   */
  private async fetchLatestVersion(): Promise<string | undefined> {
    try {
      const url = new URL('app-version.json', this.document.baseURI);
      url.searchParams.set('update-check', Date.now().toString());
      const response = await fetch(url.toString(), { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) {
        return undefined;
      }
      const body = (await response.json()) as { version?: unknown } | null;
      return typeof body?.version === 'string' && /^\d+\.\d+\.\d+/.test(body.version) ? body.version : undefined;
    } catch {
      return undefined;
    }
  }

  private openDialog(reason: AppUpdateReason): void {
    if (this.dialogRef) {
      return;
    }

    const currentVersion = this.appService.version;
    const newVersion = this.latestVersion && this.latestVersion !== currentVersion ? this.latestVersion : undefined;
    const data: AppUpdateDialogData = newVersion ? { reason, currentVersion, newVersion } : { reason, currentVersion };
    this._dialogOpen.set(true);
    this.dialogRef = this.matDialog.open<AppUpdateDialogComponent, AppUpdateDialogData, boolean>(
      AppUpdateDialogComponent,
      {
        data,
        hasBackdrop: true,
        backdropClass: 'egi-dialog-backdrop',
        // Zelfde paneel als e-dialog: de shell (e-dialog-container) tekent rand en schaduw.
        panelClass: 'e-dialog-panel',
        // Focus op de dialoog zelf, niet op een knop: wie net aan het typen is, mag niet
        // met een Enter per ongeluk herstarten (en niet-bewaard werk verliezen).
        autoFocus: 'dialog'
      }
    );
    this.dialogRef.afterClosed().subscribe(restart => this.onDialogClosed(restart === true));
  }

  private onDialogClosed(restart: boolean): void {
    const broadcast = !this.closingOnBehalfOfOtherTab;
    this.closingOnBehalfOfOtherTab = false;
    this.dialogRef = undefined;
    this._dialogOpen.set(false);

    if (broadcast) {
      currentTab.notify(restart ? 'update:restart' : 'update:dismiss');
    }

    if (restart) {
      this.reloader.reloadToCurrentUrl();
      return;
    }

    this.dismissedBuildKey = this.latestBuildKey;
    this.chunkFailed = false;
  }

  /** Een keuze in een andere tab geldt ook voor de dialoog die hier openstaat. */
  private listenForDecisionsInOtherTabs(): void {
    const subscription = currentTab.messageFromOtherTab$.subscribe(message => {
      if (!this.dialogRef || (message !== 'update:restart' && message !== 'update:dismiss')) {
        return;
      }

      this.zone.run(() => {
        this.closingOnBehalfOfOtherTab = true;
        this.dialogRef?.close(message === 'update:restart');
      });
    });
    this.cleanups.push(() => subscription.unsubscribe());
  }

  private listen(target: EventTarget, type: string, handler: (event: Event) => void): void {
    target.addEventListener(type, handler);
    this.cleanups.push(() => target.removeEventListener(type, handler));
  }

  private stop(): void {
    this.cleanups.splice(0).forEach(cleanup => cleanup());
    this.started = false;
  }
}
